import { PortableText } from "@portabletext/react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Animate } from "../../components/animate";
import { Layout } from "../../components/layout";
import { articleBySlugQuery } from "../../lib/queries";
import { client, urlFor } from "../../lib/sanityClient";

const categoryStyles = {
  "Product Update": "bg-primary/10 text-primary",
  "Finance Tips": "bg-green/10 text-green",
  "Company News": "bg-pink/10 text-pink",
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-NG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const portableTextComponents = {
  block: {
    h2: ({ children }) => (
      <h2 className="text-deep-primary font-medium text-2xl lg:text-3xl tracking-[-0.02em] mt-10 mb-4">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-deep-primary font-medium text-xl lg:text-2xl tracking-[-0.01em] mt-8 mb-3">
        {children}
      </h3>
    ),
    normal: ({ children }) => (
      <p className="text-grey-700 text-base lg:text-lg leading-[28px] lg:leading-[32px] mb-5">
        {children}
      </p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-primary pl-5 my-6 text-grey-600 italic text-lg">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside text-grey-700 text-base lg:text-lg leading-[28px] mb-5 space-y-2 pl-2">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside text-grey-700 text-base lg:text-lg leading-[28px] mb-5 space-y-2 pl-2">
        {children}
      </ol>
    ),
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold text-deep-primary">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    link: ({ value, children }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline hover:text-primary/80 transition-colors"
      >
        {children}
      </a>
    ),
  },
};

export const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .fetch(articleBySlugQuery, {
        slug,
        test: process.env.NODE_ENV === "development",
      })
      .then((data) => {
        if (!data) navigate("/blog", { replace: true });
        else setArticle(data);
      })
      .catch(() => navigate("/blog", { replace: true }))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <Layout>
        <div className="max-width py-[80px] animate-pulse space-y-6">
          <div className="h-4 w-24 bg-grey-100 rounded" />
          <div className="h-10 w-2/3 bg-grey-100 rounded-lg" />
          <div className="h-4 w-40 bg-grey-100 rounded" />
          <div className="w-full aspect-[16/6] bg-grey-100 rounded-[24px]" />
          <div className="space-y-3 max-w-[720px]">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`h-4 bg-grey-100 rounded ${i % 3 === 2 ? "w-3/4" : "w-full"}`}
              />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (!article) return null;

  const pillStyle =
    categoryStyles[article.category] || "bg-grey-100 text-grey-700";

  return (
    <Layout>
      <div className="w-full">
        {/* Back link */}
        <div className="max-width pt-8 pb-2">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-grey-500 hover:text-primary transition-colors text-sm font-medium"
          >
            ← Back to News
          </Link>
        </div>

        {/* Article header */}
        <div className="max-width py-8 lg:py-12">
          <Animate animation="fade-down">
            <div className="max-w-[760px] flex flex-col gap-5">
              {article.category && (
                <span
                  className={`w-fit text-xs font-semibold px-3 py-1 rounded-full ${pillStyle}`}
                >
                  {article.category}
                </span>
              )}
              <h1 className="text-deep-primary font-medium text-[36px] lg:text-[56px] tracking-[-0.03em] leading-[40px] lg:leading-[60px]">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-grey-500 text-sm">
                {article.publishedAt && (
                  <span>{formatDate(article.publishedAt)}</span>
                )}
                {article.publishedAt && article.readTime && <span>·</span>}
                {article.readTime && <span>{article.readTime} min read</span>}
              </div>
            </div>
          </Animate>
        </div>

        {/* Cover image */}
        {article.coverImage && (
          <div className="max-width pb-12">
            <div className="w-full aspect-[16/6] rounded-[24px] overflow-hidden">
              <img
                src={urlFor(article.coverImage)
                  .width(1280)
                  .height(480)
                  .fit("crop")
                  .url()}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Article body */}
        <div className="max-width pb-[80px]">
          <div className="max-w-[760px]">
            {article.body && (
              <PortableText
                value={article.body}
                components={portableTextComponents}
              />
            )}
          </div>

          {/* Footer */}
          <div className="max-w-[760px] mt-12 pt-8 border-t border-grey-200">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
            >
              ← Back to all articles
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};
